import { FC, useEffect } from 'react';
import { BasicAlert, CollapsiblePanel, ICollapsiblePanelComponentProps, Markdown } from '@ngt/forms';
import { Box, Button, Chip, Divider, Paper, styled, Tooltip, Typography, lighten, darken } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat, faTableList } from '@fortawesome/pro-duotone-svg-icons';
import { ISourceDataFormCollectionComponentProps } from '@/utilities/forms/SourceDataFormProcessor';
import { faCheckCircle, faExclamationCircle, faExclamationTriangle, faInfoCircle } from '@fortawesome/pro-light-svg-icons';

const Title = styled(Typography)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(2)
}));

const IconContainer = styled(Box)(({ theme }) => ({
    marginRight: theme.spacing(2)
}));

const TitleChip = styled(Chip)(({ theme }) => ({
    marginLeft: 'auto'
}));

const RepeatChip = styled(Chip)(({ auto, theme }: any) => ({
    marginLeft: auto ? 'auto' : theme.spacing(1)
}));

const Content = styled(Paper)(({ theme }) => ({
    margin: theme.spacing(0, 0, 0, 0),
    background: theme.palette.mode === 'dark' ? 
        lighten(theme.palette.common.black, 0.18) :
        darken(theme.palette.common.white, 0.03)
}));


const SourceDataFormCollectionTitled: FC<ISourceDataFormCollectionComponentProps> = ({
    formSectionLabel,
    formSectionRepeat,
    formSectionConfiguration,
    children
}) => {

    return (
        <Paper square>
            <Title variant="h4">
                <IconContainer>
                    <FontAwesomeIcon icon={faTableList} />
                </IconContainer>
                {formSectionLabel}
                {
                    formSectionRepeat && (
                        <Tooltip title="Repeat">
                            <RepeatChip
                                // @ts-ignore
                                auto="1"
                                icon={<FontAwesomeIcon icon={faRepeat} />}
                                label={formSectionRepeat}
                            />
                        </Tooltip>
                    )
                }
            </Title>
            <Content>
                {
                    !formSectionRepeat && (
                        <BasicAlert
                            title="No Records"
                            description={`There has been no data recorded in OpenClinica for ${formSectionLabel}.`}
                            severity="info"
                            icon={faInfoCircle}
                        />
                    )
                }
                { 
                    !!formSectionConfiguration?.alertInformation && (
                        <BasicAlert
                            description={<Markdown.Preview>{formSectionConfiguration.alertInformation}</Markdown.Preview>}
                            severity="info"
                            icon={faInfoCircle}
                        />
                    )
                }
                { 
                    !!formSectionConfiguration?.alertWarning && (
                        <BasicAlert
                            description={<Markdown.Preview>{formSectionConfiguration.alertWarning}</Markdown.Preview>}
                            severity="warning"
                            icon={faExclamationCircle}
                        />
                    )
                }
                { 
                    !!formSectionConfiguration?.alertError && (
                        <BasicAlert
                            description={<Markdown.Preview>{formSectionConfiguration.alertError}</Markdown.Preview>}
                            severity="error"
                            icon={faExclamationTriangle}
                        />
                    )
                }
                { 
                    !!formSectionConfiguration?.alertSuccess && (
                        <BasicAlert
                            description={<Markdown.Preview>{formSectionConfiguration.alertSuccess}</Markdown.Preview>}
                            severity="success"
                            icon={faCheckCircle}
                        />
                    )
                }
                {children}
            </Content>
            <Divider />
        </Paper>
    );
}

export default SourceDataFormCollectionTitled;